import {OrderedMap} from 'immutable';
import {kebabCase} from 'lodash';
import {
  LARGER_BREAKPOINTS,
  getOrderedBreakpointIdsByInheritanceRules,
} from '@packages/systems/style/media-queries';

import type {BreakpointID, CssPropertyMap} from '@packages/systems/style/types';
import {
  matchExpression,
  Expressions,
  type ExpressionPath,
} from '@packages/systems/wfdl';
import type {PseudoStateKey} from '@packages/systems/core/utils/StyleTypes';
import {NO_PSEUDO} from '@packages/systems/core/utils/StyleUtils';

import {weakMemo, cacheMemo} from '@packages/utilities/memo';
import {
  isBody,
  isFormForm,
  isListItem,
} from '@packages/utilities/elementTypeUtils';
import {identity} from '@packages/utilities/fp/utils';

import {
  type Expression,
  type ExpressionElement,
} from '@packages/systems/wfdl-ast';
import {COMBINATORS} from '@packages/systems/core/constants/Selectors';
import {StyleBlock} from '@packages/systems/style-legacy/records';
import {VariantStateKey} from '@packages/systems/style-legacy/selectors';
import {createVariantKey} from '@packages/systems/style-legacy/variants';

export * from './delete-style-blocks';
export * from './update-style-rule';
export * from './update-many-style-rules';
export * from './reify-style-block';
export * from './reset-tag-style-block';

const isElementTypeMap = new Map([
  [isBody, 'default-body'],
  [isFormForm, 'default-form'],
  [isListItem, 'default-li'],
]);

const findGlobalClassKey = (element: ExpressionElement) => {
  for (const [isElementTypeGlobal, globalClassKey] of isElementTypeMap) {
    if (isElementTypeGlobal(element)) {
      return globalClassKey;
    }
  }
  return null;
};

const getCssPropertyForMediaQuery = (
  styleBlockGuid: string,
  breakpointId: BreakpointID,
  pseudoMode: PseudoStateKey | null | undefined,
  variantState: VariantStateKey | null,
  cssPropertyMap: CssPropertyMap
) => {
  let cssProperties = null;

  // First, attempt to get the property from the current breakpoint+pseudo+variant state
  const variantKey = createVariantKey({
    mediaQuery: breakpointId,
    pseudoState: pseudoMode,
    variantState,
  });

  cssProperties = cssPropertyMap.getIn([styleBlockGuid, variantKey]);

  // If no properties are set there, then attempt to get the property from the current breakpoint
  if (cssProperties == null) {
    cssProperties = cssPropertyMap.getIn([styleBlockGuid, breakpointId]);
  }

  if (cssProperties) return cssProperties;

  // If no properties are set from above, fall back to the higher breakpoint:
  const breakpointIds = getOrderedBreakpointIdsByInheritanceRules(breakpointId);
  const index = breakpointIds.indexOf(breakpointId);

  return getCssPropertyMapForBreakpointIndex(
    styleBlockGuid,
    cssPropertyMap,
    breakpointIds,
    index - 1
  );
};

// @ts-expect-error - TS7023 - 'getCssPropertyMapForBreakpointIndex' implicitly has return type 'any' because it does not have a return type annotation and is referenced directly or indirectly in one of its return expressions.
const getCssPropertyMapForBreakpointIndex = (
  styleBlockGuid: string,
  cssPropertyMap: CssPropertyMap,
  breakpointIds: Array<BreakpointID>,
  index: number
) => {
  return index <= 0
    ? cssPropertyMap.getIn([styleBlockGuid, breakpointIds[0]])
    : cssPropertyMap.getIn([styleBlockGuid, breakpointIds[index]]) ||
        getCssPropertyMapForBreakpointIndex(
          styleBlockGuid,
          cssPropertyMap,
          breakpointIds,
          index - 1
        );
};

const memoMediaQueryProp = cacheMemo(LARGER_BREAKPOINTS.length);
const memoPseudoProp = cacheMemo(8);
const memoVariantProp = cacheMemo(10);
const getList = matchExpression({
  List: identity,
  _: (_) => [],
});

const generateStylePath = (
  breakpointId: BreakpointID,
  pseudoKey: PseudoStateKey,
  variantState?: VariantStateKey | null
): ExpressionPath => {
  const variantOrBasePath: ExpressionPath = variantState
    ? [
        {in: 'Record', at: 'variants'},
        {in: 'Record', at: variantState},
      ]
    : [{in: 'Record', at: 'base'}];

  return [
    {in: 'Record', at: 'style'},
    ...variantOrBasePath,
    {in: 'Record', at: breakpointId},
    {in: 'Record', at: pseudoKey},
  ] satisfies ExpressionPath;
};

type LocalStyles = {
  [key: string]: string;
};
type LocalStylesArgs<Target, Styles> = {
  target: Target;
  getStyles: (
    arg1: Target,
    arg2: BreakpointID,
    arg3: PseudoStateKey | null,
    arg4?: VariantStateKey | null
  ) => Styles;
  mergeStyles: (arg1: Styles, arg2: Styles) => Styles;
};

const _getElementLocalStyles = (
  element: ExpressionElement,
  breakpointId: BreakpointID,
  pseudoMode?: PseudoStateKey | null,
  variantState?: VariantStateKey | null
): LocalStyles => {
  const idStylesData = Expressions.getIn(
    Expressions.getElementData(element),
    generateStylePath(breakpointId, pseudoMode || NO_PSEUDO, variantState)
  );

  if (!idStylesData) {
    return {};
  }

  const idStylesRecord = Expressions.getRecord(idStylesData);

  if (!idStylesRecord) {
    return {};
  }

  return Object.entries(idStylesRecord).reduce<Record<string, any>>(
    (localStyles, [key, expr]: [any, any]) => {
      const value = Expressions.getText(expr);
      if (value) {
        localStyles[kebabCase(key)] = value;
      }
      return localStyles;
    },
    {}
  );
};

/**
 * Helper method to get an Element local styles at the given breakpoint ID
 */
const getLocalStyles = (
  element: ExpressionElement,
  breakpointId: BreakpointID,
  pseudoMode?: PseudoStateKey | null,
  variantState?: VariantStateKey | null
): LocalStyles => {
  const baseLocalStyles = _getElementLocalStyles(
    element,
    breakpointId,
    pseudoMode
  );

  return variantState
    ? {
        ...baseLocalStyles,
        ..._getElementLocalStyles(
          element,
          breakpointId,
          pseudoMode,
          variantState
        ),
      }
    : baseLocalStyles;
};

/**
 * Helper method to merge Element local styles into one object.
 */
const mergeLocalStyles = (
  styles: LocalStyles,
  localStyles: LocalStyles
): LocalStyles => {
  return {...styles, ...localStyles};
};

/**
 * Helper method to get all target Element local styles that cascade from all
 * breakpoints following media query inheritance rules.
 * Accepts a target Element and a getter function to retrieve local styles at a
 * given breakpoint ID / pseudo mode.
 */
const getAllLocalStyles = <Target, Styles>(
  args: LocalStylesArgs<Target, Styles>,
  breakpointId: BreakpointID,
  pseudoMode?: PseudoStateKey | null,
  variantState?: VariantStateKey | null
): Styles => {
  const breakpointIds = getOrderedBreakpointIdsByInheritanceRules(breakpointId);
  const breakpointIndex = breakpointIds.indexOf(breakpointId);
  return getLocalStylesRecursively<Target, Styles>(
    args,
    pseudoMode,
    breakpointIds,
    breakpointIndex,
    variantState
  );
};

/**
 * Recursively look up local styles on the given list of breakpoint IDs starting
 * at the given index.
 */
const getLocalStylesRecursively = <Target, Styles>(
  {target, getStyles, mergeStyles}: LocalStylesArgs<Target, Styles>,
  pseudoMode: PseudoStateKey | null | undefined,
  breakpointIds: BreakpointID[],
  breakpointIndex: number,
  variantState?: VariantStateKey | null
): Styles => {
  // Stop the recursion if we're at index 0 (which should be 'main'
  // and return current styles
  if (breakpointIndex <= 0) {
    // @ts-expect-error - TS2345 - Argument of type 'BreakpointID | undefined' is not assignable to parameter of type 'BreakpointID'.
    return getStyles(target, breakpointIds[0], pseudoMode, variantState);
  }

  return mergeStyles(
    // Recursively look up local styles for larger breakpoints
    getLocalStylesRecursively(
      {target, getStyles, mergeStyles},
      pseudoMode,
      breakpointIds,
      breakpointIndex - 1,
      variantState
    ),
    // Get local styles at current breakpoint. These override the
    // inherited values from larger breakpoints because they have higher specificity.
    // @ts-expect-error - TS2345 - Argument of type 'BreakpointID | undefined' is not assignable to parameter of type 'BreakpointID'.
    getStyles(target, breakpointIds[breakpointIndex], pseudoMode, variantState)
  );
};

/**
 * Returns a map of CSS property-values for the given element at the given
 * breakpoint ID, pseudo mode, and variant state.
 *
 * ⚠️ **WARNING**: This function does NOT properly implement the full CSS
 * cascade at this time.
 *
 * Unaccounted for cascade logic includes but is not limited to:
 *
 * - Class names that match but aren't explicitly part of the element's
 *   `styleBlockIds` list
 * - Styles applied without pseudo state or variant state (when `pseudoMode` or
 *   `variantState` is passed in)
 */
export const getStyleProperties = memoMediaQueryProp(
  (breakpointId: BreakpointID) =>
    memoPseudoProp((pseudoMode?: PseudoStateKey | null) =>
      memoVariantProp((variantState: VariantStateKey | null) =>
        weakMemo((cssPropertyMap: CssPropertyMap) =>
          weakMemo((element: ExpressionElement) => {
            const styleBlockIdsListExpression = Expressions.getIn(
              Expressions.getElementData(element),
              [{in: 'Record', at: 'styleBlockIds'}]
            );

            const elementStyleBlockIds = styleBlockIdsListExpression
              ? getList(styleBlockIdsListExpression)
              : [];

            let globalStyleMap: OrderedMap<string, string> = OrderedMap();

            // Since global selectors have the least specificity, we create the
            // style property map for them first if the node in question is
            // global, and then downstream selectors override them.
            const globalClassKey = findGlobalClassKey(element);

            if (globalClassKey) {
              globalStyleMap = globalStyleMap.merge(
                getCssPropertyForMediaQuery(
                  globalClassKey,
                  breakpointId,
                  pseudoMode,
                  variantState,
                  cssPropertyMap
                )
              );
            }

            // Global styles overridden with class selectors.
            const updatedStyleMap = elementStyleBlockIds.reduce(
              (
                styleMap: OrderedMap<string, string>,
                styleBlockGuidExpr: Expression
              ): OrderedMap<string, string> => {
                const styleBlockGuid = Expressions.getText(styleBlockGuidExpr);

                return styleBlockGuid
                  ? styleMap.merge(
                      getCssPropertyForMediaQuery(
                        styleBlockGuid,
                        breakpointId,
                        pseudoMode,
                        variantState,
                        cssPropertyMap
                      )
                    )
                  : styleMap;
              },
              globalStyleMap
            );

            // Override with local id selectors which have the highest specificity.
            const localStyles = getAllLocalStyles<
              ExpressionElement,
              LocalStyles
            >(
              {
                target: element,
                getStyles: getLocalStyles,
                mergeStyles: mergeLocalStyles,
              },
              breakpointId,
              pseudoMode,
              variantState
            );

            return updatedStyleMap.merge(localStyles);
          })
        )
      )
    )
);

export const isComboClass = (styleBlock: StyleBlock) => {
  if (!styleBlock || !styleBlock.type) return false;

  return Boolean(
    styleBlock.type === 'class' &&
    styleBlock.comb !== COMBINATORS.NONE &&
    styleBlock.comb
  );
};

export const isElementStyle = (styleBlock: StyleBlock) => {
  if (!styleBlock || !styleBlock.type) return false;

  return styleBlock.type === 'element';
};

export const forTestSuite = {
  getCssPropertyForMediaQuery,
};
